import React, { useState } from "react";
import { toast } from "sonner";
import { KeyRound, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import SignaturePad from "@/components/SignaturePad";

const API = process.env.REACT_APP_BACKEND_URL;

/* Reception hands the tablet to the customer: name, km, signature → card gets a pickup record. */
export default function PickupDialog({ card, onDone, trigger }) {
  const { t, meta } = useLang();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(card.customer_name || "");
  const [km, setKm] = useState(card.car_km || "");
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/repairs/${card.id}/pickup`, { signed_by: name.trim(), km: km === "" ? null : Number(km), signature_data_url: sig });
      toast.success(t("pickupDone")); setOpen(false); onDone?.(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (card.pickup) {
    const p = card.pickup;
    return (
      <div className="flex items-center gap-3 rounded-xl border border-emerald-500/40 bg-emerald-500/5 px-3 py-2 text-sm" data-testid="pickup-done">
        <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-medium">{t("pickedUpBy")} {p.signed_by}{p.km ? ` · ${p.km} km` : ""}</div>
          <div className="text-xs text-muted-foreground">{new Date(p.at).toLocaleString(meta.locale, { dateStyle: "medium", timeStyle: "short" })} · {p.by}</div>
        </div>
        {p.signature_photo_id && <img src={`${API}/api/photos/${p.signature_photo_id}`} alt="" className="h-10 w-24 object-contain bg-white rounded border border-border" data-testid="pickup-signature-img" />}
      </div>
    );
  }
  return (
    <>
      {trigger ? React.cloneElement(trigger, { onClick: () => setOpen(true) }) : (
        <Button variant="outline" className="rounded-full border-emerald-500/50 text-emerald-700 dark:text-emerald-400" onClick={() => setOpen(true)} data-testid="pickup-open"><KeyRound className="h-4 w-4 mr-2" />{t("pickupBtn")}</Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="pickup-dialog">
          <DialogHeader>
            <DialogTitle>{t("pickupTitle")}</DialogTitle>
            <DialogDescription>{[card.car_make, card.car_model].filter(Boolean).join(" ")} · {card.car_plate} — {t("pickupHint")}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1"><Label className="text-xs">{t("pickupSignedBy")}</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="h-11" data-testid="pickup-name" /></div>
            <div className="space-y-1"><Label className="text-xs">{t("kmAtPickup")}</Label><Input type="number" min={0} value={km} onChange={(e) => setKm(e.target.value)} className="h-11" data-testid="pickup-km" /></div>
          </div>
          <SignaturePad onChange={setSig} label={t("signHere")} clearLabel={t("clearSignature")} />
          <p className="text-[11px] text-muted-foreground">{t("pickupLegal")}</p>
          <Button className="h-12 rounded-full" disabled={!sig || !name.trim() || busy} onClick={submit} data-testid="pickup-submit"><CheckCircle2 className="h-4 w-4 mr-2" />{t("pickupConfirm")}</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
